import React, { useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import withConfig from "../config-validation/withConfig";
import ToolbarSocialButton from "./FluentSocialButton";
import ToolbarChatButton from "./FluentChatButton";
import { useSocialContext } from "../contexts/SocialContext";
import AiAgents from "../../singleton/AIAgents";
import voiceSynthesizer from "../../singleton/VoiceSynthetizer";
import { AGENT_EVENTS } from "../../utils/agent-utils";
import configManager from "../../singleton/ConfigManager";
import { canSendDialogChat, canShowDialogChat } from "./chat-visibility-utils";

export function SocialButtonContainer({
  styles,
  scene,
  hubChannel,
  mySessionId,
  people,
  presences,
  entered,
  dialogConnected,
  hasDialogConnectedOnce,
  participantLayoutRoomEnabled,
  participantLayoutForcedState,
  onParticipantLayoutRoomStateChange
}) {
  const agents = AiAgents.getAgents();

  // Only load voices when there are agents
  useEffect(() => {
    if (agents.length > 0) {
      voiceSynthesizer.loadVoices();
    }
  }, [agents]);

  // const {
  //   closeChat
  // } = useSocialContext();

  // const updateAgents = () => {
  //   setAgents()
  //   // const aiEntities = Array.from(getAiEntities());
  //   // const aiIds = aiEntities.map(entity => entity.components["npc-avatar"].npcId);

  //   // // Add new AI entities
  //   // aiEntities.forEach(entity => {
  //   //   if (entity.components["npc-avatar"] && !agents.some(agent => agent.id === entity.components["npc-avatar"].npcId)) {
  //   //     setAgents(prevAgents => [
  //   //       ...prevAgents,
  //   //       {
  //   //         id: entity.components["npc-avatar"].npcId,
  //   //         name: entity.components["npc-avatar"].name,
  //   //         isAi: true,
  //   //         thumbnail: AILogo
  //   //       }
  //   //     ]);
  //   //   }
  //   // });

  //   // // Remove AI entities that are no longer present
  //   // setAgents(prevAgents => prevAgents.filter(agent => aiIds.includes(agent.id)));
  // }

  // useEffect(() => {
  //   updateAgents();
  // }, []);

  // useEffect(() => {
  //   scene.addEventListener("environment-scene-loaded", event => {
  //     updateAgents();
  //     closeChat({clearUsers:true});

  //   });
  // }, [scene]);

  return (
    <SocialContent
      agents={agents}
      people={people}
      styles={styles}
      scene={scene}
      hubChannel={hubChannel}
      mySessionId={mySessionId}
      presences={presences}
      entered={entered}
      dialogConnected={dialogConnected}
      hasDialogConnectedOnce={hasDialogConnectedOnce}
      participantLayoutRoomEnabled={participantLayoutRoomEnabled}
      participantLayoutForcedState={participantLayoutForcedState}
      onParticipantLayoutRoomStateChange={onParticipantLayoutRoomStateChange}
    />
  );
}

// Separate component to use the context
function SocialContent({
  agents,
  people,
  styles,
  scene,
  hubChannel,
  mySessionId,
  entered,
  dialogConnected,
  hasDialogConnectedOnce,
  participantLayoutRoomEnabled,
  participantLayoutForcedState,
  onParticipantLayoutRoomStateChange
}) {
  const {
    isOpenChat,
    isOpenParticipants,
    toggleParticipants,
    closeParticipants,
    isOpenSettings,
    setIsOpenSettings,
    openChatUser,
    toggleChat,
    selectedUserChat,
    closeChat
  } = useSocialContext();
  const canOpenChat = canShowDialogChat({
    textChatEnabled: !!configManager.config?.textChat?.enabled,
    entered,
    hasDialogConnectedOnce
  });
  const canSendChat = canSendDialogChat({
    textChatEnabled: !!configManager.config?.textChat?.enabled,
    entered,
    dialogConnected
  });

  const onAiProximity = useCallback(
    async event => {
      if (!canOpenChat) {
        return;
      }

      const selectedAgent = agents.find(agent => agent?.id === event.detail);
      voiceSynthesizer.agent = selectedAgent;
      openChatUser(selectedAgent);

      await new Promise(resolve => setTimeout(resolve, 0));
      window.dispatchEvent(new CustomEvent("ai-proximity-voice"));
    },
    [agents, canOpenChat, openChatUser]
  );

  useEffect(() => {
    window.addEventListener(AGENT_EVENTS.AI_ON_ENTER_PROXIMITY, onAiProximity);
    return () => {
      window.removeEventListener(AGENT_EVENTS.AI_ON_ENTER_PROXIMITY, onAiProximity);
    };
  }, [onAiProximity]);

  return (
    <>
      <SocialButtonContainer.SocialButton
        agents={agents}
        people={people}
        openChatUser={openChatUser}
        styles={styles}
        scene={scene}
        hubChannel={hubChannel}
        mySessionId={mySessionId}
        isOpenChat={isOpenChat}
        isOpenParticipants={isOpenParticipants}
        toggleParticipants={toggleParticipants}
        closeParticipants={closeParticipants}
        isOpenSettings={isOpenSettings}
        setIsOpenSettings={setIsOpenSettings}
        canOpenChat={canOpenChat}
        participantLayoutRoomEnabled={participantLayoutRoomEnabled}
        participantLayoutForcedState={participantLayoutForcedState}
        onParticipantLayoutRoomStateChange={onParticipantLayoutRoomStateChange}
      />
      <SocialButtonContainer.ToolbarChatButton
        isOpenChat={isOpenChat}
        isOpenParticipants={isOpenParticipants}
        toggleChat={toggleChat}
        closeChat={closeChat}
        canOpenChat={canOpenChat}
        canSendChat={canSendChat}
        styles={styles}
        scene={scene}
        hubChannel={hubChannel}
        mySessionId={mySessionId}
        selectedUser={selectedUserChat}
      />
    </>
  );
}

SocialButtonContainer.propTypes = {
  styles: PropTypes.object,
  scene: PropTypes.object,
  hubChannel: PropTypes.object,
  mySessionId: PropTypes.string,
  people: PropTypes.array.isRequired,
  presences: PropTypes.object,
  entered: PropTypes.bool,
  dialogConnected: PropTypes.bool,
  hasDialogConnectedOnce: PropTypes.bool,
  participantLayoutRoomEnabled: PropTypes.bool,
  participantLayoutForcedState: PropTypes.string,
  onParticipantLayoutRoomStateChange: PropTypes.func
};

SocialContent.propTypes = {
  agents: PropTypes.array.isRequired,
  people: PropTypes.array.isRequired,
  styles: PropTypes.object,
  scene: PropTypes.object,
  hubChannel: PropTypes.object,
  mySessionId: PropTypes.string,
  entered: PropTypes.bool,
  dialogConnected: PropTypes.bool,
  hasDialogConnectedOnce: PropTypes.bool,
  participantLayoutRoomEnabled: PropTypes.bool,
  participantLayoutForcedState: PropTypes.string,
  onParticipantLayoutRoomStateChange: PropTypes.func
};

SocialButtonContainer.SocialButton = withConfig(ToolbarSocialButton, `toolbar.socialButton.enabled`);
SocialButtonContainer.ToolbarChatButton = withConfig(ToolbarChatButton, `textChat.enabled`);
